import React, { useEffect, useState } from 'react';
import type { EmThread } from '../../email/types';
import { useEmailAttachmentPicker, type EmailComposeAttachment } from '../../email/composeAttachments';
import { emIsNonEnglish, emLangLabel } from '../../email/utils';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { EmailAttachmentList, EmailAttachmentPickerInput } from './EmailAttachmentPicker';

type ReplyMode = 'reply' | 'replyAll' | 'forward';

const MODE_LABEL: Record<ReplyMode, string> = { reply: 'Reply', replyAll: 'Reply all', forward: 'Forward' };

export interface EmailReplySendPayload {
  body: string;
  attachments?: EmailComposeAttachment[];
}

export interface EmailInlineComposerProps {
  thread: EmThread;
  mode: ReplyMode;
  setMode: (mode: ReplyMode) => void;
  draft: EmThread['draft'] | null;
  onClose: () => void;
  onSend?: (payload: EmailReplySendPayload) => void;
  sending?: boolean;
}

export function EmailInlineComposer({
  thread,
  mode,
  setMode,
  draft,
  onClose,
  onSend,
  sending = false,
}: EmailInlineComposerProps) {
  const [body, setBody] = useState(draft ? draft.body : '');
  const [showWhy, setShowWhy] = useState(true);
  const {
    inputRef: attachInputRef,
    attachments,
    openPicker,
    onFilesSelected,
    removeAttachment,
    clearAttachments,
  } = useEmailAttachmentPicker();

  useEffect(() => {
    setBody(draft ? draft.body : '');
    clearAttachments();
  }, [draft, thread.id, clearAttachments]);

  const isDraft = !!draft;
  const to = mode === 'forward' ? '' : thread.email;
  const handleSend = () => {
    if (sending || !onSend) return;
    onSend({
      body: body.trim(),
      ...(attachments.length ? { attachments } : {}),
    });
    clearAttachments();
  };

  return (
    <div className={`em-reply ${isDraft ? 'draft' : ''} em-rise`}>
      {isDraft && showWhy ? (
        <div
          style={{
            padding: '9px 14px',
            fontSize: 12,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            background: 'color-mix(in srgb, var(--gold) 7%, transparent)',
            borderBottom: '1px solid var(--cream-2)',
          }}
        >
          <Icon kind="info" size={13} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 1 }} />
          <span style={{ flex: 1 }}>
            <b style={{ color: 'var(--ink-1)' }}>Review before sending:</b> {draft.reason}
          </span>
          <button
            type="button"
            onClick={() => setShowWhy(false)}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink-4)', display: 'inline-flex', flex: 'none' }}
          >
            <Icon kind="x" size={12} />
          </button>
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderBottom: '1px solid var(--cream-2)' }}>
        <div
          style={{
            display: 'inline-flex',
            gap: 2,
            background: 'var(--cream-0)',
            borderRadius: 'var(--r-pill)',
            padding: 2,
            border: '1px solid var(--cream-2)',
            flex: 'none',
          }}
        >
          {(['reply', 'replyAll', 'forward'] as const).map((mo) => (
            <button
              key={mo}
              type="button"
              onClick={() => setMode(mo)}
              style={{
                padding: '4px 10px',
                borderRadius: 'var(--r-pill)',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 600,
                fontFamily: 'var(--font-body)',
                background: mode === mo ? 'var(--cream-1)' : 'transparent',
                color: mode === mo ? 'var(--ink-0)' : 'var(--ink-3)',
                boxShadow: mode === mo ? 'inset 0 0 0 1px var(--cream-2)' : 'none',
              }}
            >
              {MODE_LABEL[mo]}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {mode === 'forward' ? (
            <span style={{ fontStyle: 'italic' }}>To: add recipients…</span>
          ) : (
            <>
              to <b style={{ color: 'var(--ink-1)', fontWeight: 600 }}>{thread.name}</b>{' '}
              <span style={{ fontFamily: 'var(--font-mono)' }}>{to}</span>
            </>
          )}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink-4)', display: 'inline-flex', flex: 'none' }}
        >
          <Icon kind="x" size={14} />
        </button>
      </div>

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={`Write your ${MODE_LABEL[mode].toLowerCase()}…`}
        style={{
          width: '100%',
          minHeight: 230,
          padding: '14px 16px',
          border: 0,
          outline: 'none',
          background: 'var(--cream-0)',
          resize: 'vertical',
          fontFamily: 'var(--font-body)',
          fontSize: 14,
          color: 'var(--ink-1)',
          lineHeight: 1.65,
          boxSizing: 'border-box',
        }}
      />

      <EmailAttachmentList attachments={attachments} onRemove={removeAttachment} />

      <div
        style={{
          padding: '11px 14px',
          borderTop: '1px solid var(--cream-2)',
          background: 'var(--cream-1)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <Button variant="primary" size="sm" iconLeft="send" disabled={sending} loading={sending} onClick={handleSend}>
          {isDraft ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Approve &amp; send
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  paddingLeft: 8,
                  borderLeft: '1px solid rgba(255,255,255,0.3)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  fontWeight: 600,
                  opacity: 0.95,
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: draft.confidence >= 70 ? '#7be0a4' : '#ffd479',
                  }}
                />
                {draft.confidence}% confidence
              </span>
            </span>
          ) : (
            'Send'
          )}
        </Button>
        <IconButton icon="attach" size={32} title="Attach" onClick={openPicker} />
        <Button variant="secondary" size="sm" iconLeft="sparkle">
          Generate by Elva
        </Button>
        {emIsNonEnglish(thread.detectedLang ?? thread.lang) ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: 'var(--ai-3)', fontWeight: 600 }}>
            <Icon kind="translate" size={12} />
            Patient writes in {emLangLabel(thread.detectedLang ?? thread.lang)}
          </span>
        ) : null}
        <span style={{ flex: 1 }} />
        <button type="button" className="em-act" style={{ border: 0 }} onClick={onClose}>
          {isDraft ? 'Discard draft' : 'Cancel'}
        </button>
      </div>
      <EmailAttachmentPickerInput inputRef={attachInputRef} onFilesSelected={onFilesSelected} />
    </div>
  );
}
